import { getDeskareAccessToken, AuthError } from "../auth/officeAuth";
import { AppError, ErrorCode, Errors } from "../errors/AppError";
import { API_CONFIG } from "../constants";

interface ApiRequestOptions extends Omit<RequestInit, "body"> {
  body?: Record<string, unknown>;
}

interface ApiErrorResponse {
  message?: string;
  errors?: Record<string, string[]>;
}

export class ApiClient {
  private static instance: ApiClient;
  private readonly baseUrl: string;

  private constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;

    if (!this.baseUrl) {
      console.warn("[ApiClient] APP_URL is not configured");
    }
  }

  static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  async request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const token = await this.getAuthToken();
    const url = this.buildUrl(endpoint);
    const headers = this.buildHeaders(token, options.headers);

    const response = await this.executeRequest(url, {
      ...options,
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      // Force Bearer-only auth: never forward a Laravel session cookie that would
      // trigger Sanctum's SPA flow and require an X-XSRF-TOKEN header we don't have.
      credentials: "omit",
    });

    return this.handleResponse<T>(response);
  }

  async get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : "";
    return this.request<T>(`${endpoint}${queryString}`);
  }

  async post<T>(endpoint: string, body: Record<string, unknown>): Promise<T> {
    return this.request<T>(endpoint, { method: "POST", body });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }

  private async getAuthToken(): Promise<string> {
    try {
      return await getDeskareAccessToken();
    } catch (error) {
      if (error instanceof AuthError) {
        throw mapAuthErrorToAppError(error);
      }
      throw Errors.authFailed(error instanceof Error ? error : undefined);
    }
  }

  private buildUrl(endpoint: string): string {
    const normalizedEndpoint = endpoint.startsWith("/api/v1")
      ? endpoint
      : `/api/v1/${endpoint.replace(/^\//, "")}`;

    return `${this.baseUrl}${normalizedEndpoint}`;
  }

  private buildHeaders(token: string, customHeaders?: HeadersInit): Record<string, string> {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      ...(customHeaders as Record<string, string>),
    };
  }

  private async executeRequest(url: string, options: RequestInit): Promise<Response> {
    try {
      return await fetch(url, options);
    } catch (error) {
      throw Errors.networkError(error instanceof Error ? error : undefined);
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      throw await this.parseErrorResponse(response);
    }

    try {
      return await response.json() as T;
    } catch {
      throw Errors.apiError("Invalid response format from server");
    }
  }

  private async parseErrorResponse(response: Response): Promise<AppError> {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    let details = "";

    // Try to parse JSON error response
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      try {
        const errorData = await response.json() as ApiErrorResponse;
        if (errorData.message) {
          message = errorData.message;
        }
        if (errorData.errors) {
          details = Object.values(errorData.errors).flat().join(", ");
        }
      } catch {
        // Fallback to text if JSON parsing fails
        try {
          details = await response.text();
        } catch {
          // Ignore parse errors
        }
      }
    } else {
      // Try to get text response
      try {
        details = await response.text();
      } catch {
        // Ignore parse errors
      }
    }

    const fullMessage = details ? `${message}\n\nDetails: ${details}` : message;
    const code = this.mapStatusToErrorCode(response.status, message);
    return new AppError(fullMessage, code);
  }

  private mapStatusToErrorCode(status: number, message: string): ErrorCode {
    if (status === 401 && (message.trim() === "Unauthenticated." || message.trim() === "Non authentifié.")) {
      return ErrorCode.ACCOUNT_NOT_FOUND;
    }

    if (status === 401 || status === 403) {
      return ErrorCode.AUTH_FAILED;
    }

    if (status === 409 || message.includes("existe déjà") || message.includes("already exists")) {
      return ErrorCode.BOOKING_CONFLICT;
    }

    if (status === 422) {
      return ErrorCode.VALIDATION_FAILED;
    }

    if (status >= 500) {
      return ErrorCode.API_ERROR;
    }

    return ErrorCode.UNKNOWN;
  }
}

function mapAuthErrorToAppError(error: AuthError): AppError {
  switch (error.code) {
    case "USER_CANCELLED":
      return Errors.userCancelled();
    case "ACCOUNT_NOT_SUPPORTED":
      return Errors.accountNotSupported();
    case "POPUP_BLOCKED":
      return Errors.popupBlocked();
    case "CONSENT_REQUIRED":
      return Errors.consentRequired();
    case "API_NOT_AVAILABLE":
      return Errors.apiNotAvailable();
    case "INTERACTION_REQUIRED":
      return new AppError(error.message, ErrorCode.AUTH_REQUIRED);
    case "CONFIG_MISSING":
    case "ALL_STRATEGIES_FAILED":
    case "UNKNOWN":
    default:
      return Errors.authFailed(error);
  }
}

export const apiClient = ApiClient.getInstance();
